import { FC, useRef, useCallback, ReactNode } from 'react'
import { useIntersection, useUpdateEffect, useMountedState } from 'react-use'
import createReducerContext from '@g4g/utils/src/react/createReducerContext'
import Spinner from '../internal/Spinner'
import AttentionBox from './AttentionBox'

interface InfinityScrollState {
  loading: boolean
  error?: Error
}

export type InfinityScrollAction =
  | { type: 'trigger-intersection'; loading: boolean }
  | { type: 'fetch-more-error'; error: Error }

const reducer = (state: InfinityScrollState, action: InfinityScrollAction): InfinityScrollState => {
  switch (action.type) {
    case 'trigger-intersection':
      return { ...state, loading: action.loading }
    case 'fetch-more-error':
      return { ...state, loading: false, error: action.error }
    default:
      throw new Error(`Unexpected infinity scroll reducer action: ${JSON.stringify(action)}`)
  }
}

const [useInfinityScroll, useInfinityScrollDispatch, InfinityScrollProvider] = createReducerContext(
  reducer,
  { loading: false, error: undefined },
  'InfinityScroll'
)

const useSafeDispatch = () => {
  const isMounted = useMountedState()
  const dispatch = useInfinityScrollDispatch()
  return useCallback(
    (action: InfinityScrollAction) => {
      isMounted() && dispatch(action)
    },
    [dispatch, isMounted]
  )
}

const InfinityScrollIntersection: FC = () => {
  const dispatch = useInfinityScrollDispatch()
  const intersectionRef = useRef(null)
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  })

  useUpdateEffect(() => {
    if (!intersection) {
      return
    }

    if (intersection.intersectionRatio === 1) {
      dispatch({ type: 'trigger-intersection', loading: true })
    }
  }, [intersection, dispatch])

  return (
    // Intersection area, fetchMore gets triggered
    // when this div is fully inside the viewport
    <div ref={intersectionRef} className="w-full h-4"></div>
  )
}

const InfinityScrollContent: FC<{
  hasNextPage: boolean
  fetchMore: Function
  children?: ReactNode
}> = ({ children, fetchMore, hasNextPage }) => {
  const { loading, error } = useInfinityScroll()
  const dispatch = useSafeDispatch()

  useUpdateEffect(() => {
    if (loading) {
      ;(async () => {
        try {
          await fetchMore()
          dispatch({ type: 'trigger-intersection', loading: false })
        } catch (error: any) {
          dispatch({ type: 'fetch-more-error', error })
          console.log(error)
        }
      })()
    }
  }, [loading, fetchMore])

  if (error) {
    return (
      <>
        {children}
        <AttentionBox>Something went wrong when trying to fetch more results</AttentionBox>
      </>
    )
  }

  return (
    <>
      {children}
      {hasNextPage && (
        <div className="flex justify-center w-full p-6 text-white">
          {loading ? <Spinner className="w-6 h-6" /> : <InfinityScrollIntersection />}
        </div>
      )}
    </>
  )
}

const InfinityScroll: FC<{ hasNextPage: boolean; fetchMore: Function; children?: ReactNode }> = ({
  children,
  fetchMore,
  hasNextPage,
}) => (
  <InfinityScrollProvider>
    <InfinityScrollContent hasNextPage={hasNextPage} fetchMore={fetchMore}>
      {children}
    </InfinityScrollContent>
  </InfinityScrollProvider>
)

export default InfinityScroll
